import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle, Crown, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const FEATURES = {
    free: [
        "Access to all 13+ course tracks",
        "Unlimited practice tests",
        "1 free full mock test (lifetime)",
        "Basic AI feedback",
    ],
    premium: [
        "Unlimited full-length mock exams",
        "Advanced AI-powered mistake analysis",
        "Personalised 7-day study plans",
        "Priority question generation",
        "Downloadable performance transcript",
    ],
};

export default function PricingPage() {
    const nav = useNavigate();
    const { user, refresh } = useAuth();
    const [loading, setLoading] = useState(null);

    const startCheckout = async (plan) => {
        if (!user) { nav("/login"); return; }
        setLoading(plan);
        try {
            const { data } = await api.post("/payments/checkout", {
                plan,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start checkout");
        } finally { setLoading(null); }
    };

    return (
        <div className="min-h-screen paper-grain" data-testid="pricing-page">
            <Navbar />
            <section className="mx-auto max-w-5xl px-6 py-20">
                <div className="text-center">
                    <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Tuition</div>
                    <h1 className="font-serif text-5xl md:text-6xl text-ink">Simple, fair pricing.</h1>
                    <p className="text-ink/70 mt-4 max-w-2xl mx-auto">
                        Start free. Upgrade when you're ready for exam-day intensity.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-14">
                    {/* Free */}
                    <div className="border border-ink/15 rounded-sm p-8 bg-bone" data-testid="plan-free">
                        <div className="text-xs uppercase tracking-widest text-ink/50">Foundation</div>
                        <div className="font-serif text-4xl text-ink mt-2">Free <span className="text-lg text-ink/40">forever</span></div>
                        <p className="text-sm text-ink/60 mt-2">Learn the ropes and prove the platform.</p>
                        <ul className="mt-6 space-y-2 text-sm">
                            {FEATURES.free.map(f => (
                                <li key={f} className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-ink/70 mt-0.5"/>{f}</li>
                            ))}
                            <li className="flex items-start gap-2 text-ink/50"><XCircle size={16} className="mt-0.5"/>Limited to 1 mock exam</li>
                        </ul>
                        <Button variant="outline" className="w-full mt-8 rounded-sm border-ink/30 text-ink h-11" onClick={() => nav("/courses")} data-testid="btn-plan-free">
                            Continue free
                        </Button>
                    </div>

                    {/* Premium */}
                    <div className="border-2 border-ink rounded-sm p-8 bg-ink text-bone relative" data-testid="plan-premium">
                        <div className="absolute -top-3 left-8 bg-gold text-ink px-3 py-1 text-[10px] uppercase tracking-widest font-medium rounded-sm">Recommended</div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold">
                            <Crown size={14} weight="fill"/> Premium
                        </div>
                        <div className="font-serif text-4xl mt-2">$9.99<span className="text-lg text-bone/50">/mo</span></div>
                        <p className="text-sm text-bone/60 mt-2">Or $79/year — save two months.</p>
                        <ul className="mt-6 space-y-2 text-sm">
                            {FEATURES.premium.map(f => (
                                <li key={f} className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-gold mt-0.5"/>{f}</li>
                            ))}
                        </ul>
                        <div className="mt-8 flex flex-col gap-2">
                            <Button onClick={() => startCheckout("premium_monthly")} disabled={loading != null || user?.is_premium} className="w-full bg-gold text-ink hover:bg-gold/90 rounded-sm h-11" data-testid="btn-plan-monthly">
                                {user?.is_premium ? "Already premium" : loading === "premium_monthly" ? "Loading Stripe…" : "Upgrade — $9.99 / month"}
                            </Button>
                            <Button onClick={() => startCheckout("premium_yearly")} disabled={loading != null || user?.is_premium} variant="ghost" className="w-full rounded-sm h-11 text-bone hover:bg-bone/10" data-testid="btn-plan-yearly">
                                {loading === "premium_yearly" ? "Loading Stripe…" : "Save with yearly — $79"}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="mt-10 text-xs text-ink/50 text-center">
                    Powered by Stripe. Test mode active — use card <span className="font-mono">4242 4242 4242 4242</span>, any future date, any CVC.
                </div>
            </section>
            <Footer />
        </div>
    );
}

export function PaymentSuccessPage() {
    const location = useLocation();
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [status, setStatus] = useState("polling");
    const [attempts, setAttempts] = useState(0);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");
        if (!sessionId) { setStatus("error"); return; }
        let cancelled = false;
        const poll = async () => {
            for (let i = 0; i < 15 && !cancelled; i++) {
                setAttempts(i + 1);
                try {
                    const { data } = await api.get(`/payments/status/${sessionId}`);
                    if (data.payment_status === "paid") {
                        setStatus("paid");
                        await refresh();
                        return;
                    }
                } catch { /* ignore */ }
                await new Promise(r => setTimeout(r, 2000));
            }
            if (!cancelled) setStatus("timeout");
        };
        poll();
        return () => { cancelled = true; };
    }, [location.search, refresh]);

    return (
        <div className="min-h-screen" data-testid="payment-success-page">
            <Navbar />
            <div className="mx-auto max-w-xl px-6 py-24 text-center">
                {status === "polling" && (<>
                    <div className="dot-pulse mx-auto"></div>
                    <div className="font-serif text-3xl text-ink mt-6">Confirming your payment…</div>
                    <div className="text-sm text-ink/50 mt-2">Attempt {attempts} / 15</div>
                </>)}
                {status === "paid" && (<>
                    <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-700"/>
                    <div className="font-serif text-4xl text-ink mt-6">You're now Premium.</div>
                    <div className="text-ink/60 mt-2">Every course, every mock, no limits.</div>
                    <Button className="mt-8 bg-ink text-bone hover:bg-ink/90 rounded-sm h-11" onClick={() => nav("/dashboard")} data-testid="btn-goto-dashboard">Go to dashboard</Button>
                </>)}
                {status === "timeout" && (<>
                    <div className="font-serif text-3xl text-ink">Still processing…</div>
                    <div className="text-sm text-ink/60 mt-2">Refresh in a minute — Stripe sometimes takes a moment.</div>
                    <Button className="mt-6 rounded-sm" variant="outline" onClick={() => nav("/dashboard")} data-testid="btn-goto-dashboard">Back to dashboard</Button>
                </>)}
                {status === "error" && <div className="font-serif text-3xl text-ink">Missing session.</div>}
            </div>
        </div>
    );
}

export function PaymentCancelPage() {
    const nav = useNavigate();
    return (
        <div className="min-h-screen" data-testid="payment-cancel-page">
            <Navbar />
            <div className="mx-auto max-w-xl px-6 py-24 text-center">
                <XCircle size={56} className="mx-auto text-oxblood"/>
                <div className="font-serif text-4xl text-ink mt-6">Payment cancelled.</div>
                <div className="text-ink/60 mt-2">No charge was made. You can try again anytime.</div>
                <Button className="mt-8 bg-ink text-bone hover:bg-ink/90 rounded-sm h-11" onClick={() => nav("/pricing")} data-testid="btn-retry">Back to pricing</Button>
            </div>
        </div>
    );
}
